'use client';
import { useMemo, useState } from 'react';
import { AiOutlineLock } from 'react-icons/ai';
import { useBenzingaEdge } from '@benzinga/edge';
import { GovernmentTrade, MemberTrade, Security, SecurityTrader } from '@benzinga/gov-trades';

import { TradeRow, TraderRow } from '../../../../src/components/GovTrades';
import { Widget, WidgetFooterButton, WidgetTab } from './Widget';

interface SidebarWidgetTab {
  key: string;
  name: string;
  data: GovernmentTrade[] | SecurityTrader[] | Security[] | MemberTrade[];
}

export interface SidebarWidgetProps {
  tabsData: SidebarWidgetTab[];
  hideWidgetHeader?: boolean;
  hideShowMore?: boolean;
}

export const SidebarWidget = ({ tabsData, hideWidgetHeader = false, hideShowMore = false }: SidebarWidgetProps) => {
  const [activeTab, setActiveTab] = useState(tabsData[0].key);
  const [limit, setLimit] = useState(5);
  const { hasEdgeSubscription: hasPermission } = useBenzingaEdge();

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
  };

  const activeTabData = useMemo(() => {
    return tabsData.find(tab => tab.key === activeTab);
  }, [activeTab, tabsData]);

  const moreToShow = () => {
    return (activeTabData?.data?.length ?? 0) > limit;
  };

  return (
    <Widget
      className="h-full min-h-0"
      scrollable
      title={hideWidgetHeader ? undefined : activeTabData?.name}
      tabs={
        tabsData.length > 1
          ? tabsData.map(tab => (
              <WidgetTab active={activeTab === tab.key} key={tab.key} onClick={() => handleTabChange(tab.key)}>
                {tab.name}
              </WidgetTab>
            ))
          : undefined
      }
      footer={
        hideShowMore ? undefined : (
          <WidgetFooterButton disabled={!hasPermission || !moreToShow()} onClick={() => setLimit(prev => prev + 5)}>
            {!hasPermission && <AiOutlineLock size={14} />}
            {moreToShow() ? 'Show More' : 'Showing all'}
          </WidgetFooterButton>
        )
      }
    >
      {activeTab === 'trades'
        ? activeTabData?.data?.slice(0, limit).map((trade: any, index: number) => <TradeRow key={index} {...trade} />)
        : activeTabData?.data
            ?.slice(0, limit)
            .map((trader: any, index: number) => <TraderRow key={index} {...trader} type={activeTab} />)}
    </Widget>
  );
};
